import { useState } from "react";
import { Mail, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { AddressLink } from "@/components/AddressLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const referralTypes = [
  { value: "self", label: "I am seeking help for myself" },
  { value: "loved_one", label: "For a loved one" },
  { value: "professional", label: "Professional / clinical referral" },
  { value: "other", label: "Other inquiry" },
];

const homePrefs = [
  { value: "male_manor", label: "Male Manor — Petersburg, VA" },
  { value: "female_home", label: "Female Home (interest list)" },
  { value: "either", label: "Either / not sure yet" },
];

export default function Contact() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", referral_type: "self", home_preference: "male_manor", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/referrals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === "string" ? data.detail : "Please check the form and try again.");
      }
      toast.success("Referral received", { description: "A confirmation email is on its way to you, and our team will reach out within one business day.", duration: 7000 });
      setForm({ full_name: "", email: "", phone: "", referral_type: "self", home_preference: "male_manor", message: "" });
    } catch (err) {
      toast.error("Something went wrong", { description: err.message });
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-purple-200 bg-white px-4 py-3 text-sm text-[#1E1B4B] focus:outline-none focus:ring-2 focus:ring-[#5B21B6] focus:border-transparent transition-shadow";

  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden bg-[#FAF8F5] grain">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#FEF3C7] blur-3xl opacity-80" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-16 lg:pt-24 text-center relative">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#B45309] mb-4">Referrals & Contact</p>
          <MaskedLines
            className="font-display font-black text-[#2E1065] text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight"
            lines={["The door is open.", "Walk through it."]}
          />
          <Reveal delay={0.4}>
            <p className="mt-6 text-base sm:text-lg text-slate-700 max-w-2xl mx-auto" data-testid="contact-intro">
              Refer a client, ask about a bed, or simply start a conversation. Every message is read by a human
              who cares.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-5 gap-12">
        <Reveal className="lg:col-span-3">
          <form onSubmit={submit} data-testid="referral-contact-form" className="bg-white rounded-3xl border border-purple-100 shadow-xl p-7 sm:p-10 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#2E1065] mb-2">Full Name *</label>
                <input data-testid="input-full-name" required value={form.full_name} onChange={set("full_name")} className={inputCls} placeholder="Jane Doe" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#2E1065] mb-2">Phone *</label>
                <input data-testid="input-phone" required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(804) 555-0142" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#2E1065] mb-2">Email *</label>
              <input data-testid="input-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.org" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#2E1065] mb-2">Referral Type</label>
                <select data-testid="select-referral-type" value={form.referral_type} onChange={set("referral_type")} className={inputCls}>
                  {referralTypes.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#2E1065] mb-2">Home Preference</label>
                <select data-testid="select-home-preference" value={form.home_preference} onChange={set("home_preference")} className={inputCls}>
                  {homePrefs.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#2E1065] mb-2">Message</label>
              <textarea data-testid="input-message" rows={5} value={form.message} onChange={set("message")} className={inputCls} placeholder="Tell us a little about the situation — everything shared is confidential." />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="submit-referral-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#5B21B6] hover:bg-[#2E1065] disabled:opacity-60 text-white font-bold px-8 py-4 rounded-full shadow-lg shadow-purple-500/25 transition-colors duration-300"
            >
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {sending ? "Sending..." : "Send Referral"}
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-2">
          <div className="space-y-5">
            <div className="bg-[#2E1065] rounded-3xl p-8 text-[#FAF8F5] grain relative overflow-hidden" data-testid="direct-contact-card">
              <h3 className="font-display font-bold text-2xl mb-6">Talk to us directly</h3>
              <ul className="space-y-5 text-sm">
                <li className="flex items-start gap-3">
                  <Mail size={18} className="text-amber-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-bold">Email</p>
                    <p className="text-purple-200" data-testid="contact-email">admin@jaliyahshouseofhopecom.org</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-amber-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-bold">The Male Manor</p>
                    <p className="text-purple-200" data-testid="contact-address">
                      <AddressLink testId="contact-address-link" className="hover:text-amber-300" />
                    </p>
                    <p className="text-[11px] text-purple-300 mt-1">Tap the address for Google Maps directions</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-[#FEF3C7] border border-amber-200 rounded-3xl p-7" data-testid="crisis-note">
              <h4 className="font-display font-bold text-lg text-[#B45309] mb-2">In crisis right now?</h4>
              <p className="text-sm text-amber-900 leading-relaxed">
                If you or someone you love is in immediate danger, call 911. For emotional support, call or text
                the 988 Suicide & Crisis Lifeline — free, confidential, 24/7.
              </p>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
